import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import { ConsultationRecordListItem } from "common/models/types";
import {
  getConsultationStatusLabel,
  getConsultationStatusColor,
} from "api/consultationService";

interface ConsultationRecordSectionProps {
  records: ConsultationRecordListItem[];
  onSelectRecord: (recordId: number) => void;
}

// --- Styled Components ---

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
`;

const RecordList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RecordItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")[50]};
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")[10]};
  }
`;

const RecordDate = styled.span`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")[500]};
  white-space: nowrap;
`;

const StatusBadge = styled.span<{ $color: string }>`
  font-size: 12px;
  font-weight: 500;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => $color}1A;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
`;

const formatDate = (date: Date | string): string => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${h}:${min}`;
};

const ConsultationRecordSection: React.FC<ConsultationRecordSectionProps> = ({
  records,
  onSelectRecord,
}) => {
  // 녹음 기록만 — 텍스트 기록(상담 체크리스트)은 「AI 검토 이력」 보고서 줄 칸에서 연다(설계 결정 20).
  const audioRecords = records.filter((record) => record.source !== "text");

  return (
    <div>
      <SectionHeader>
        <Text textType="H3" color={COLOR_SYSTEM.get("Gray")[700]} element="h3">
          상담 녹음 기록
        </Text>
      </SectionHeader>

      {audioRecords.length === 0 ? (
        <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")[300]}>
          등록된 상담 녹음 기록이 없습니다.
        </Text>
      ) : (
        <RecordList>
          {audioRecords.map((record) => {
            const statusColor = getConsultationStatusColor(record.status);
            const statusLabel = getConsultationStatusLabel(record.status);
            return (
              <RecordItem
                key={record.id}
                data-testid="consultation-record-item"
                onClick={() => onSelectRecord(record.id)}
              >
                <RecordDate>{formatDate(record.createdAt)}</RecordDate>
                <StatusBadge $color={statusColor}>{statusLabel}</StatusBadge>
              </RecordItem>
            );
          })}
        </RecordList>
      )}
    </div>
  );
};

export default ConsultationRecordSection;
